import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  LoadingIndicator,
  whitelistedStatusCodes,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { useNavigate } from 'react-router-dom';
import {
  Resolver,
  FieldErrors,
  FieldError,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { SignUpStep } from '../../../common/provider/SignUpContext';
import { useSignUp } from '../../../common/hooks/useSignUp';
import { ActionBar } from '../ActionBar';
import { SIGN_UP_SELECT_PLAN_FORM } from '../../../common/constants';
import {
  ApiErrorResponse,
  ProductFamily,
  SignupProductType,
  SignupV2Request,
  SolutionType,
} from '../../../common/api/types';
import { getProductPlans, initialSignUpV2 } from '../../../common/api/sign-up';
import { stepRouteMapping } from '../../signUpUtility';
import { AddonPlanCard } from './AddonPlanCard';
import { PlanCard } from './PlanCard';
import {
  FormFamily,
  SelectPlanForm,
  SelectedAddon,
  SelectedSolution,
  SolutionPlanItem,
} from '../types';
import { ClearSelectedPlanModal } from './ClearSelectedPlanModal';
import { ProductFamilyTabGroup } from './ProductTabGroup';
import { ExpandablePlanWrapper } from './ExpandablePlanWrapper';
import { Header } from '../Header';

enum SelectedLabel {
  Trial,
  Starter,
  Growth,
  Pro,
}

export interface PendingSolutionSelection {
  familyName: string;
  solutions: SolutionPlanItem[];
  addons: string[];
}

const selectPlanFromResolver: Resolver<SelectPlanForm> = (values) => {
  const errors = {} as FieldErrors<SelectPlanForm>;
  if (
    !values ||
    !values.families ||
    values.families.length <= 0 ||
    !values?.families?.some(({ solutions }) =>
      solutions.some(({ item }) => item?.selectedTier !== undefined)
    )
  ) {
    errors.families = {
      message: 'No solution selected',
    } as FieldError;
  }
  return { values, errors };
};

export const SelectPlanComponent: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    setSignUpStep,
    solutionId,
    tierName,
    signUpCustomerEmail,
    signUpCustomerCountry,
    setErrorMessage,
    setFormId,
    setSelectedSolutionType,
    setAreOrgDetailsValid,
    setAreBillingDetailsValid,
  } = useSignUp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [productFamilies, setProductFamilies] = useState<ProductFamily[]>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    trigger,
    formState: { isValid },
  } = useForm<SelectPlanForm>({
    mode: 'onChange',
    defaultValues: {
      families: [],
    },
    resolver: selectPlanFromResolver,
  });

  const { fields, replace } = useFieldArray({
    control,
    name: 'families',
  });

  useEffect(() => {
    if (signUpCustomerEmail && signUpCustomerCountry) {
      const readProductPlans = async () => {
        const families = await getProductPlans(
          signUpCustomerCountry,
          signUpCustomerEmail
        );

        if (families && families.length > 0) {
          setProductFamilies(families);
          const familyNameBasedOnSolutionId = families.find(({ solutions }) =>
            solutions.some(({ id }) => id === solutionId)
          )?.name;
          const familyFormArray: FormFamily[] = [];
          const rawData = sessionStorage.getItem(SIGN_UP_SELECT_PLAN_FORM);
          if (rawData) {
            const dataObject: SelectPlanForm = JSON.parse(rawData);
            dataObject?.families.forEach((family) => {
              familyFormArray.push(family);
            });
          }

          families.forEach(({ name, solutions }, i) => {
            if (
              !familyFormArray.some(
                ({ name: existingName }) => existingName === name
              )
            ) {
              const addons = solutions.flatMap(({ addons }) => {
                return addons.map((addon) => {
                  return { item: { ...addon, selectedTier: undefined } };
                });
              });

              const solutionsDto = solutions.map((solution) => {
                if (solution.id === solutionId) {
                  const selectedTier = solution.tiers.find(
                    ({ label }) => label === tierName
                  )?.tierId;
                  return { item: { ...solution, selectedTier } };
                }
                return { item: { ...solution, selectedTier: undefined } };
              });

              familyFormArray.push({
                selected: familyNameBasedOnSolutionId
                  ? name === familyNameBasedOnSolutionId
                  : i === 0,
                addons: addons,
                solutions: solutionsDto,
                name,
              });
            }
          });
          replace(familyFormArray);
        }
      };
      readProductPlans();
    }
  }, [
    tierName,
    signUpCustomerEmail,
    signUpCustomerCountry,
    solutionId,
    replace,
  ]);

  const currentlySelectedSolutions = fields.flatMap((_, i) => {
    return watch(`families.${i}.solutions`);
  });

  const currentSelectedSolutionLable = useMemo(() => {
    const selectedLabelList: SelectedLabel[] = [];
    fields
      .find(({ selected }) => selected)
      ?.solutions.forEach(({ item: { tiers } }) => {
        const matchingLabel = tiers?.find(({ tierId }) =>
          currentlySelectedSolutions?.some(
            ({ item }) => item.selectedTier === tierId
          )
        )?.label;
        if (matchingLabel) {
          selectedLabelList.push(SelectedLabel[matchingLabel]);
        }
      });

    return SelectedLabel[Math.max(...selectedLabelList)];
  }, [fields, currentlySelectedSolutions]);

  const handleClearSelectionClick = useCallback(
    (familyIndex: number | undefined, solutionIndex: number | undefined) => {
      if (familyIndex !== undefined && solutionIndex !== undefined) {
        const matchingField = fields[familyIndex].solutions[solutionIndex];
        matchingField.item.selectedTier = undefined;
        setValue(
          `families.${familyIndex}.solutions.${solutionIndex}`,
          matchingField
        );
        trigger(`families.${familyIndex}.solutions`);
      } else {
        const cleanFields = fields.map(
          ({ name, selected, solutions, addons }) => {
            return {
              name,
              selected,
              solutions: solutions.map(({ item }) => {
                return { item: { ...item, selectedTier: undefined } };
              }),
              addons: addons.map(({ item }) => {
                return { item: { ...item, selectedTier: undefined } };
              }),
            };
          }
        );
        reset({ families: cleanFields });
      }
    },
    [reset, setValue, trigger, fields]
  );

  const navigateToNextStep = useCallback(
    (formId: string, solutionType: SolutionType, type: SignupProductType) => {
      setFormId(formId);
      setSignUpStep(SignUpStep.orgDetails);
      if (solutionType) {
        setSelectedSolutionType(solutionType, type);
      }
      setAreOrgDetailsValid(false);
      setAreBillingDetailsValid(false);
      navigate(stepRouteMapping[SignUpStep[SignUpStep.orgDetails]]);
    },
    [
      setSignUpStep,
      setFormId,
      setSelectedSolutionType,
      navigate,
      setAreOrgDetailsValid,
      setAreBillingDetailsValid,
    ]
  );

  const onSubmit = handleSubmit(async ({ families }: SelectPlanForm) => {
    try {
      setIsLoading(true);
      sessionStorage.setItem(
        SIGN_UP_SELECT_PLAN_FORM,
        JSON.stringify({ families })
      );
      const solutions =
        families
          .find(({ selected }) => selected)
          ?.solutions?.filter(({ item: { selectedTier } }) => !!selectedTier) ||
        [];
      const addons =
        families
          .find(({ selected }) => selected)
          ?.addons?.filter(({ item: { selectedTier } }) => !!selectedTier) ||
        [];
      const request = mapProductsFromSelectedIds(solutions, addons);
      const response = await initialSignUpV2(
        signUpCustomerEmail as string,
        signUpCustomerCountry as string,
        request
      );
      if (!response?.validation?.valid && response?.validation?.violations) {
        response.validation.violations.forEach((violation) =>
          setError(violation.element as keyof SelectPlanForm, {
            message: violation.violation,
          })
        );
      } else if (response.waitlist) {
        setSignUpStep(SignUpStep.waitlisted);
        navigate(stepRouteMapping[SignUpStep[SignUpStep.waitlisted]]);
      } else {
        navigateToNextStep(
          response.uuid as string,
          solutions[0]?.item?.solutionType,
          solutions[0]?.item?.type
        );
      }
      setIsLoading(false);
    } catch (e: any) {
      if (whitelistedStatusCodes.includes(e?.request?.status)) {
        const errorResponse: ApiErrorResponse = JSON.parse(e.request.response);
        setErrorMessage(errorResponse.error.description);
      } else {
        setErrorMessage('Please go back to the form and try again');
      }
      setIsLoading(false);
    }
  });

  const handleOnContinue = useCallback(() => {
    const cleanFields = fields.map(({ name, selected, solutions, addons }) => {
      return {
        name,
        selected,
        solutions: solutions.map(({ item }) => {
          return { item: { ...item, selectedTier: undefined } };
        }),
        addons: addons.map(({ item }) => {
          return { item: { ...item, selectedTier: undefined } };
        }),
      };
    });
    reset({ families: cleanFields });
    trigger(`families`);
    setIsModalOpen(false);
  }, [fields, reset, trigger]);

  const handleOnGoBack = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  if (isLoading || !productFamilies) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:h-full w-full content-between items-center">
      <Header
        subtitle="Pick from the list of products below to continue."
        title="Select your plan"
      />
      <form className="flex flex-col pt-10 w-full h-full">
        <ClearSelectedPlanModal
          onContinue={handleOnContinue}
          onGoBack={handleOnGoBack}
          isModalOpen={isModalOpen}
        />
        <div className="pb-6 px-8">
          <ProductFamilyTabGroup replace={replace} watch={watch} />
          <hr />
        </div>
        {fields.map(({ selected, solutions, addons }, u) => {
          if (selected) {
            const addonSelected = addons?.some(
              ({ item: { selectedTier } }) => !!selectedTier
            );
            return (
              <div
                className="flex-1 flex flex-col gap-y-4 lg:pb-8 h-full overflow-auto mb-28 lg:mb-60 px-8"
                key={u}
              >
                {solutions.map(
                  ({ item: { tiers, displayName, shortDescription } }, i) => {
                    const isSelected = tiers?.some(({ tierId: tId }) =>
                      currentlySelectedSolutions?.find(
                        ({ item }) => item.selectedTier === tId
                      )
                    );
                    const selectedLabel = tiers?.find(({ tierId: tId }) =>
                      currentlySelectedSolutions?.find(
                        ({ item }) => item.selectedTier === tId
                      )
                    )?.label;
                    return (
                      <ExpandablePlanWrapper
                        clearSelection={handleClearSelectionClick}
                        displayName={displayName}
                        shortDescription={shortDescription}
                        familyIndex={u}
                        key={i}
                        totalSolutionCount={solutions.length}
                        selectedSolutionLabel={selectedLabel}
                        solutionIndex={i}
                        isSelected={isSelected}
                      >
                        <div className="flex flex-col gap-y-1">
                          {tiers.map((tier, index) => (
                            <PlanCard
                              control={control}
                              inputKey={`families.${u}.solutions.${i}`}
                              key={index}
                              openCleanSolutionModal={() =>
                                setIsModalOpen(true)
                              }
                              tier={tier}
                              watch={watch}
                            />
                          ))}
                        </div>
                      </ExpandablePlanWrapper>
                    );
                  }
                )}
                {addons && addons.length > 0 && (
                  <ExpandablePlanWrapper
                    displayName=""
                    shortDescription=""
                    totalSolutionCount={0}
                    isAddon={true}
                    isSelected={addonSelected}
                  >
                    <div className="flex flex-col gap-y-1">
                      {addons?.map(({ item }, i) => (
                        <AddonPlanCard
                          item={item}
                          control={control}
                          key={i}
                          inputKey={`families.${u}.addons.${i}`}
                          openCleanSolutionModal={() => setIsModalOpen(true)}
                          selectedSolutionLabel={currentSelectedSolutionLable}
                          watch={watch}
                        />
                      ))}
                    </div>
                  </ExpandablePlanWrapper>
                )}
              </div>
            );
          }
          return null;
        })}
      </form>
      <ActionBar onSubmit={onSubmit} isValid={isValid} />
    </div>
  );
};

const mapProductsFromSelectedIds = (
  selectedSolutions: SelectedSolution[],
  selectedAddons: SelectedAddon[]
) => {
  const solutionProducts: SignupV2Request[] = [];
  selectedSolutions?.forEach(
    ({ item: { selectedTier, id, type, version, tiers } }) => {
      const matchingTier = tiers.find(({ tierId }) => tierId === selectedTier);
      if (tiers && selectedTier && matchingTier) {
        solutionProducts.push({
          id,
          type,
          tierId: selectedTier,
          versionId: version,
          tierName: matchingTier.label,
        });
      }
    }
  );
  const selectedLabelList: SelectedLabel[] = [];
  solutionProducts.forEach((product) => {
    selectedLabelList.push(SelectedLabel[product.tierName]);
  });
  const labelToMatch = SelectedLabel[Math.max(...selectedLabelList)];
  const addOnProducts: SignupV2Request[] = [];
  selectedAddons?.forEach(
    ({ item: { selectedTier, id, type, version, tiers } }) => {
      const matchingTier = tiers.find(({ label }) => label === labelToMatch);
      if (tiers && selectedTier && matchingTier) {
        addOnProducts.push({
          id,
          type,
          tierId: matchingTier.tierId,
          versionId: version,
          tierName: matchingTier.label,
        });
      }
    }
  );

  return [...solutionProducts, ...addOnProducts];
};
